/**
 * SponsorHub.tsx
 * Route: /sponsor
 *
 * The single entry point for ALL sponsorship on Sage.
 * Replaces the header dropdown link confusion.
 *
 * Model A — "You Pay Half, We Match Half"
 *   One-time gift. Sage assigns the child from the pool.
 *   Sponsor pays 50%. Sage matches 50%. Gift code issued.
 *   → Calls gift-enrolment edge function
 *   → NO recipient email field — Sage assigns from hardship pool
 *
 * Model B — Recurring Multi-Seat (Individual or Corporate)
 *   Recurring subscription. Sponsor pays per seat per billing cycle.
 *   Sage still matches. Admin assigns seats to approved hardship applicants.
 *   → Routes to /sponsor/corporate (SponsorCheckout.tsx)
 *
 * Navigation states:
 *   - Logged out: shows both options, login gated on proceed
 *   - Logged in individual: defaults to Model A
 *   - Logged in corporate/ngo/church: surfaces Model B prominently
 *   - Has existing sponsorship: shows "Manage" link to /sponsor/dashboard
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useSponsorPricing } from '@/hooks/useSponsorPricing'
import { useAuth } from '@/hooks/useAuth'
import SEO from '@/components/layout/SEO'
import LanguagePicker from '@/components/university/ui/LanguagePicker'

// ── Design tokens (Matches Still Waters University Black & Gold aesthetic) ──
const M = '#06060c' // Main Deep Background
const G = '#d4a843' // University Gold
const D = '#101010' // Card background
const BR = 'rgba(212,168,67,0.15)' // Gold tint border
const CA = 'rgba(255,255,255,0.03)' // Subtle white action background
const TX = '#ffffff' // White text
const MU = 'rgba(255,255,255,0.6)' // Muted text
const MU2 = 'rgba(255,255,255,0.3)' // Subtle text
const GR = '#d4a843' // Use gold for success too (matching university branding)
const PU = '#d4a843' // Unify on gold branding

type Tab = 'standard' | 'elite' | 'bundle'
type HowLong = 1 | 6 | 12

// ── Plan data — Model A (one-time gift, pool-assigned) ────────────────────────
// Note: sponsorCents = 50% of plan price (sponsorCents = fullCents / 2); update when subscription_plans change
const PLANS: Record<Tab, { months: HowLong; sponsorCents: number; fullCents: number; label: string; badge?: string }[]> = {
    standard: [
        { months: 1, sponsorCents: 1750, fullCents: 3500, label: '1 Month' },
        { months: 6, sponsorCents: 9450, fullCents: 18900, label: '6 Months', badge: 'MOST IMPACTFUL' },
        { months: 12, sponsorCents: 16800, fullCents: 33600, label: 'Full Year', badge: 'BEST VALUE' },
    ],
    elite: [
        { months: 1, sponsorCents: 2250, fullCents: 4500, label: '1 Month' },
        { months: 6, sponsorCents: 12150, fullCents: 24300, label: '6 Months', badge: 'MOST POPULAR' },
        { months: 12, sponsorCents: 21600, fullCents: 43200, label: 'Full Year', badge: 'BEST VALUE' },
    ],
    bundle: [
        { months: 1, sponsorCents: 2950, fullCents: 5900, label: '1 Month' },
        { months: 6, sponsorCents: 15900, fullCents: 31800, label: '6 Months', badge: 'ULTIMATE GIFT' },
        { months: 12, sponsorCents: 28250, fullCents: 56500, label: 'Full Year', badge: 'BEST VALUE' },
    ],
}

const TAB_META: Record<Tab, { emoji: string; title: string; desc: string; courses: string; color: string; bg: string; border: string }> = {
    standard: {
        emoji: '📚', title: 'Standard Curriculum',
        desc: '84 subjects — maths, science, languages, arts, history, computing and more. Full AI-guided learning from age 5 to 18.',
        courses: '84 subjects', color: G,
        bg: 'rgba(212,168,67,0.05)', border: 'rgba(212,168,67,0.15)',
    },
    elite: {
        emoji: '👑', title: 'Elite Academy',
        desc: '30 specialist courses in character, leadership and excellence — etiquette, diplomacy, rhetoric, finance, and legacy building.',
        courses: '30 elite courses', color: G,
        bg: 'rgba(212,168,67,0.08)', border: 'rgba(212,168,67,0.3)',
    },
    bundle: {
        emoji: '✨', title: 'Full Bundle',
        desc: 'Everything — all 84 standard subjects AND all 30 Elite Academy courses. The complete education.',
        courses: '114 total courses', color: G,
        bg: 'rgba(212,168,67,0.1)', border: 'rgba(212,168,67,0.4)',
    },
}

const PLAN_KEY: Record<Tab, string> = {
    standard: 'homeschool_single',
    elite: 'edu_elite',
    bundle: 'edu_bundle',
}

function fmt(cents: number) {
    const n = cents / 100
    return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`
}

export default function SponsorHub() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { t } = useTranslation()
    const pricing = useSponsorPricing()

    // Model A state
    const [activeTab, setActiveTab] = useState<Tab>('standard')
    const [months, setMonths] = useState<HowLong>(6)
    const [sponsorEmail, setSponsorEmail] = useState(user?.email || '')
    const [sponsorName, setSponsorName] = useState('')
    const [message, setMessage] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasExisting, setHasExisting] = useState(false)

    // Which model is foregrounded
    const [mode, setMode] = useState<'a' | 'b'>('a')

    const meta = TAB_META[activeTab]
    const planRow = PLANS[activeTab].find(p => p.months === months) || PLANS[activeTab][1]
    const tierPricing = pricing[activeTab]
    const bPrices = {
        mo: `$${tierPricing.month.toFixed(2)}`,
        sixmo: `$${tierPricing.six_months.toFixed(2)}`,
        yr: `$${tierPricing.year.toFixed(2).replace(/\.00$/, '')}`,
    }

    useEffect(() => {
        if (user?.email) setSponsorEmail(user.email)
    }, [user])

    // Check if user has existing Model B sponsorship
    useEffect(() => {
        if (!user) return
        supabase.from('facility_seat_sponsorships')
            .select('id', { count: 'exact', head: true })
            .eq('sponsor_id', user.id)
            .eq('status', 'active')
            .then(({ count }) => { if ((count ?? 0) > 0) setHasExisting(true) })
    }, [user])

    async function handleModelACheckout() {
        if (!user) { navigate('/auth/login?next=/sponsor/give'); return }
        if (!sponsorEmail.trim()) { setError(t('sponsor.form.emailRequired', 'Please enter your email address.')); return }
        setLoading(true); setError(null)
        try {
            const { data: { session } } = await supabase.auth.getSession()
            const supabaseUrl = (supabase as any).supabaseUrl
            const res = await fetch(`${supabaseUrl}/functions/v1/gift-enrolment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
                },
                body: JSON.stringify({
                    // No recipient_email — Sage assigns from pool
                    plan_key: PLAN_KEY[activeTab],
                    duration_months: planRow.months,
                    buyer_email: sponsorEmail.trim(),
                    buyer_name: sponsorName.trim() || undefined,
                    personal_message: message.trim() || undefined,
                    amount_cents: planRow.sponsorCents,
                    sage_match_cents: planRow.sponsorCents,
                    is_sponsorship: true,
                    pool_contribution: true,   // tells gift-enrolment NOT to send to a recipient directly
                    platform: 'sage',
                    success_url: `${window.location.origin}/sponsor/sent?plan=${activeTab}_${months}m`,
                    cancel_url: `${window.location.origin}/sponsor/give`,
                }),
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Checkout failed')
            if (data.url) window.location.href = data.url
        } catch (err: any) {
            setError(err.message)
            setLoading(false)
        }
    }

    const s = { background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }

    return (
        <div style={s}>
            <SEO title={t('sponsor.title', "Sponsor a Child's Education — Still Waters Academy")} description={t('sponsor.description', "You pay half. Sage matches half. Give a child full access to AI-guided education.")} noindex={false} />

            <div style={{ position: 'relative', zIndex: 40, maxWidth: 1100, margin: '0 auto', padding: '16px 24px 0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <LanguagePicker />
            </div>

            {/* ── Hero ─────────────────────────────────────────────────────────── */}
            <div style={{ background: `linear-gradient(165deg, #0a0a0f 0%, ${M} 100%)`, padding: '80px 24px 64px', textAlign: 'center', borderBottom: `1px solid ${BR}` }}>
                <div style={{ maxWidth: 720, margin: '0 auto' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 999, background: 'rgba(212,168,67,0.1)', border: '1px solid rgba(212,168,67,0.25)', color: G, fontSize: 11, fontWeight: 800, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 32 }}>
                        {t('sponsor.title', "Sponsor a Child's Education")}
                    </div>
                    <h1 style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', fontFamily: "'Source Serif 4', 'Playfair Display', serif", fontWeight: 700, lineHeight: 1.05, margin: '0 0 24px', letterSpacing: '-0.01em' , color: '#0ea5e9'}}>
                        {t('sponsor.headline', 'You Pay Half.')}<br />
                        <span style={{ color: G }}>{t('sponsor.matchline', 'We Match Half.')}</span>
                    </h1>
                    <p style={{ color: MU, fontSize: 19, lineHeight: 1.7, maxWidth: 600, margin: '0 auto 48px', fontWeight: 400 }}>
                        {t('sponsor.description')}
                    </p>

                    {/* How it works — University Style */}
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {[
                            { n: '01', t: t('sponsor.howItWorks.contribute'), d: t('sponsor.howItWorks.contributeDesc') },
                            { n: '02', t: t('sponsor.howItWorks.match'), d: t('sponsor.howItWorks.matchDesc') },
                            { n: '03', t: t('sponsor.howItWorks.learn'), d: t('sponsor.howItWorks.learnDesc') },
                        ].map(s => (
                            <div key={s.n} style={{ background: 'rgba(212,168,67,0.03)', border: `1px solid ${BR}`, borderRadius: 20, padding: '24px', textAlign: 'left', minWidth: 200, flex: '1 1 200px' }}>
                                <p style={{ color: G, fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: 12 }}>STEP {s.n}</p>
                                <p style={{ color: TX, fontSize: 16, fontWeight: 700, margin: '0 0 6px', fontFamily: "'Source Serif 4', serif" }}>{s.t}</p>
                                <p style={{ color: MU, fontSize: 13, lineHeight: 1.5, margin: 0 }}>{s.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Mode toggle ──────────────────────────────────────────────────── */}
            <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px 0' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
                    {([
                        {
                            id: 'a' as const,
                            icon: '🎁',
                            title: t('sponsor.modes.oneTime.title'),
                            sub: t('sponsor.modes.oneTime.subtitle'),
                            detail: t('sponsor.modes.oneTime.detail'),
                        },
                        {
                            id: 'b' as const,
                            icon: '🏢',
                            title: t('sponsor.modes.recurring.title'),
                            sub: t('sponsor.modes.recurring.subtitle'),
                            detail: t('sponsor.modes.recurring.detail'),
                        },
                    ]).map(opt => (
                        <button key={opt.id} onClick={() => setMode(opt.id)}
                            style={{
                                background: mode === opt.id ? 'rgba(212,168,67,0.06)' : 'rgba(255,255,255,0.01)',
                                border: `1.5px solid ${mode === opt.id ? G : 'rgba(212,168,67,0.1)'}`,
                                borderRadius: 22,
                                padding: '24px 20px',
                                textAlign: 'left',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: mode === opt.id ? '0 10px 30px -10px rgba(212,168,67,0.2)' : 'none',
                                transform: mode === opt.id ? 'translateY(-2px)' : 'none',
                            }}>
                            <div style={{ fontSize: 28, marginBottom: 12, filter: mode === opt.id ? 'none' : 'grayscale(1) opacity(0.5)' }}>{opt.icon}</div>
                            <p style={{ color: mode === opt.id ? TX : MU, fontWeight: 800, fontSize: 16, margin: '0 0 4px', fontFamily: "'Source Serif 4', serif" }}>{opt.title}</p>
                            <p style={{ color: mode === opt.id ? G : MU, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 10px' }}>{opt.sub}</p>
                            <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{opt.detail}</p>
                        </button>
                    ))}
                </div>
            </div>

            <div style={{ maxWidth: 720, margin: '0 auto', padding: '28px 24px 80px' }}>

                {/* ─────────────────── MODEL A ──────────────────────────────────── */}
                <AnimatePresence mode="wait">
                    {mode === 'a' && (
                        <motion.div key="model-a" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>

                            {/* Pool notice */}
                            <div style={{ background: 'rgba(212,168,67,0.04)', border: `1px solid ${BR}`, borderRadius: 18, padding: '18px 24px', marginBottom: 32, display: 'flex', gap: 16, alignItems: 'center' }}>
                                <span style={{ fontSize: 24, flexShrink: 0 }}>🌱</span>
                                <div>
                                    <p style={{ color: G, fontWeight: 800, fontSize: 14, margin: '0 0 2px', fontFamily: "'Source Serif 4', serif" }}>{t('sponsor.poolNote.title')}</p>
                                    <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                                        {t('sponsor.poolNote.detail')}
                                    </p>
                                </div>
                            </div>

                            {/* Curriculum tab picker */}
                            <p style={{ color: MU, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>{t('sponsor.steps.curriculum')}</p>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                                {(['standard', 'elite', 'bundle'] as Tab[]).map(tab => (
                                    <button key={tab} onClick={() => { setActiveTab(tab); setMonths(6) }}
                                        style={{
                                            flex: 1,
                                            padding: '10px 8px',
                                            borderRadius: 999,
                                            border: `1.5px solid ${activeTab === tab ? TAB_META[tab].color : BR}`,
                                            background: activeTab === tab ? TAB_META[tab].bg : 'transparent',
                                            color: activeTab === tab ? TAB_META[tab].color : MU,
                                            fontWeight: 700,
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s',
                                        }}>
                                        {tab === 'standard' ? t('sponsor.tabs.standard', 'Standard') : tab === 'elite' ? t('sponsor.tabs.elite', 'Elite Academy') : t('sponsor.tabs.bundle', 'Full Bundle')}
                                    </button>
                                ))}
                            </div>

                            {/* Curriculum info card */}
                            <div style={{ background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 16, padding: '16px 20px', marginBottom: 20 }}>
                                <p style={{ fontSize: 22, margin: '0 0 8px' }}>{meta.emoji}</p>
                                <p style={{ color: meta.color, fontWeight: 700, fontSize: 14, margin: '0 0 4px' }}>{meta.title} · {meta.courses}</p>
                                <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{meta.desc}</p>
                            </div>

                            {/* Duration picker */}
                            <p style={{ color: MU, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>{t('sponsor.steps.duration')}</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 28 }}>
                                {PLANS[activeTab].map(p => (
                                    <button key={p.months} onClick={() => setMonths(p.months)}
                                        style={{
                                            padding: '16px 10px',
                                            borderRadius: 16,
                                            border: `2px solid ${months === p.months ? meta.color : BR}`,
                                            background: months === p.months ? meta.bg : CA,
                                            cursor: 'pointer',
                                            position: 'relative',
                                            transition: 'all 0.15s',
                                        }}>
                                        {p.badge && (
                                            <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: meta.color, color: M, fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap', letterSpacing: '0.08em' }}>
                                                {p.badge}
                                            </div>
                                        )}
                                        <p style={{ color: months === p.months ? meta.color : MU, fontWeight: 800, fontSize: 20, margin: '0 0 2px' }}>{fmt(p.sponsorCents)}</p>
                                        <p style={{ color: MU, fontSize: 11, margin: '0 0 4px' }}>you pay</p>
                                        <p style={{ color: months === p.months ? TX : MU, fontWeight: 600, fontSize: 12, margin: 0 }}>{p.label}</p>
                                    </button>
                                ))}
                            </div>

                            {/* Match summary */}
                            <div style={{ background: 'rgba(212,168,67,0.04)', border: `1px solid ${BR}`, borderRadius: 18, padding: '24px', marginBottom: 32 }}>
                                <p style={{ color: G, fontSize: 11, fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 16, textAlign: 'center' }}>{t('sponsor.matchSummary.title')}</p>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                    {[
                                        { label: t('sponsor.matchSummary.youPay'), val: fmt(planRow.sponsorCents), color: TX },
                                        { label: t('sponsor.matchSummary.sageMatches'), val: fmt(planRow.sponsorCents), color: G },
                                        { label: t('sponsor.matchSummary.childReceives'), val: fmt(planRow.fullCents), color: G },
                                    ].map(r => (
                                        <div key={r.label} style={{ textAlign: 'center' }}>
                                            <p style={{ color: r.color, fontWeight: 800, fontSize: 20, margin: '0 0 4px', fontFamily: "'Source Serif 4', serif" }}>{r.val}</p>
                                            <p style={{ color: MU, fontSize: 11, fontWeight: 600, margin: 0 }}>{r.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Sponsor details */}
                            <p style={{ color: MU, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>{t('sponsor.steps.details')}</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
                                    <div>
                                        <label style={{ color: MU, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>{t('sponsor.form.emailLabel')}</label>
                                        <input value={sponsorEmail} onChange={e => setSponsorEmail(e.target.value)} type="email" placeholder={t('sponsor.form.emailPlaceholder')}
                                            style={{ width: '100%', background: CA, border: `1px solid ${BR}`, borderRadius: 10, padding: '11px 14px', color: TX, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                                    </div>
                                    <div>
                                        <label style={{ color: MU, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>{t('sponsor.form.nameLabel')}</label>
                                        <input value={sponsorName} onChange={e => setSponsorName(e.target.value)} placeholder={t('sponsor.form.namePlaceholder')}
                                            style={{ width: '100%', background: CA, border: `1px solid ${BR}`, borderRadius: 10, padding: '11px 14px', color: TX, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                                    </div>
                                </div>
                                <div>
                                    <label style={{ color: MU, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>{t('sponsor.form.message')}</label>
                                    <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
                                        placeholder={t('sponsor.form.messagePlaceholder')}
                                        style={{ width: '100%', background: CA, border: `1px solid ${BR}`, borderRadius: 10, padding: '11px 14px', color: TX, fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box', lineHeight: 1.5 }} />
                                    <p style={{ color: MU, fontSize: 11, margin: '6px 0 0' }}>{t('sponsor.form.messageDetail')}</p>
                                </div>
                            </div>

                            {error && (
                                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                                    <p style={{ color: '#f87171', fontSize: 13, margin: 0 }}>{error}</p>
                                </div>
                            )}

                            <button onClick={handleModelACheckout} disabled={loading}
                                style={{
                                    width: '100%', padding: '20px', borderRadius: 18,
                                    background: loading ? 'rgba(212,168,67,0.2)' : G,
                                    color: M,
                                    fontWeight: 800, fontSize: 18, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                                    letterSpacing: '-0.01em', transition: 'all 0.3s',
                                    fontFamily: "'Inter', sans-serif",
                                    boxShadow: loading ? 'none' : '0 8px 30px rgba(212,168,67,0.3)',
                                }}>
                                {loading ? t('common.redirecting', 'Redirecting to checkout…') : t('sponsor.form.paid', { amount: fmt(planRow.sponsorCents) })}
                            </button>
                            <p style={{ color: MU, fontSize: 12, textAlign: 'center', margin: '16px 0 0', fontWeight: 500 }}>
                                {t('sponsor.form.security')}
                            </p>

                            {/* Already have a code? */}
                            <div style={{ textAlign: 'center', marginTop: 28, paddingTop: 24, borderTop: `1px solid ${BR}` }}>
                                <p style={{ color: MU, fontSize: 13, margin: '0 0 8px' }}>{t('sponsor.form.hasCode')}</p>
                                <Link to="/redeem-sponsor" style={{ color: GR, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                                    {t('sponsor.form.redeem')} →
                                </Link>
                            </div>
                        </motion.div>
                    )}

                    {/* ─────────────────── MODEL B ────────────────────────────────── */}
                    {mode === 'b' && (
                        <motion.div key="model-b" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>

                            {/* Who is this for */}
                            <div style={{ background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.15)', borderRadius: 14, padding: '14px 18px', marginBottom: 24 }}>
                                <p style={{ color: G, fontWeight: 700, fontSize: 13, margin: '0 0 4px' }}>{t('sponsor.for_businesses_churches_ngos_and_committed')}</p>
                                <p style={{ color: MU, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                                    Sponsor multiple children on an ongoing basis. Your seats are held in your name. You can add or remove seats anytime. Admin assigns each seat to an approved hardship applicant from the pool.
                                </p>
                            </div>

                            {/* Curriculum selector for pricing reference */}
                            <p style={{ color: MU, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 }}>{t('sponsor.curriculum_tier')}</p>
                            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                                {(['standard', 'elite', 'bundle'] as Tab[]).map(tab => (
                                    <button key={tab} onClick={() => setActiveTab(tab)}
                                        style={{
                                            flex: 1, padding: '10px 8px', borderRadius: 999,
                                            border: `1.5px solid ${activeTab === tab ? TAB_META[tab].color : BR}`,
                                            background: activeTab === tab ? TAB_META[tab].bg : 'transparent',
                                            color: activeTab === tab ? TAB_META[tab].color : MU,
                                            fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                                        }}>
                                        {tab === 'standard' ? 'Standard' : tab === 'elite' ? 'Elite' : 'Bundle'}
                                    </button>
                                ))}
                            </div>

                            {/* Pricing grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 24 }}>
                                {[
                                    { label: 'Monthly', price: bPrices.mo, sub: 'per seat/month', badge: null },
                                    { label: '6-Monthly', price: bPrices.sixmo, sub: 'per seat', badge: 'SAVE 9%' },
                                    { label: 'Annual', price: bPrices.yr, sub: 'per seat/year', badge: 'SAVE 12%' },
                                ].map(r => (
                                    <div key={r.label} style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 14, padding: '16px 12px', textAlign: 'center', position: 'relative' }}>
                                        {r.badge && (
                                            <div style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', background: G, color: M, fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                                                {r.badge}
                                            </div>
                                        )}
                                        <p style={{ color: TX, fontWeight: 800, fontSize: 20, margin: '0 0 2px' }}>{r.price}</p>
                                        <p style={{ color: MU, fontSize: 11, margin: '0 0 4px' }}>{r.sub}</p>
                                        <p style={{ color: MU, fontSize: 12, fontWeight: 600, margin: 0 }}>{r.label}</p>
                                    </div>
                                ))}
                            </div>

                            {/* What you get */}
                            <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '20px', marginBottom: 24 }}>
                                <p style={{ color: MU, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>{t('sponsor.what_included')}</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {[
                                        { icon: '🎓', t: 'Choose Standard, Elite, or Bundle', d: 'Your seats are locked to your chosen curriculum tier' },
                                        { icon: '➕', t: 'Add or remove seats anytime', d: 'Your billing adjusts immediately on your next cycle' },
                                        { icon: '📊', t: 'Sponsor dashboard', d: 'View anonymised or named progress — your choice at sign-up' },
                                        { icon: '🏢', t: 'Corporate recognition', d: 'Logo and social tagging on impact posts if you opt in' },
                                        { icon: '🤝', t: 'You choose how seats are assigned', d: 'Nominate specific children yourself, or let our team assign from the verified hardship pool' },
                                        { icon: '💚', t: 'We still match', d: 'Your 50% contribution is matched by Still Waters Academy for every seat' },
                                    ].map(f => (
                                        <div key={f.t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{f.icon}</span>
                                            <div>
                                                <p style={{ color: TX, fontWeight: 600, fontSize: 13, margin: '0 0 2px' }}>{f.t}</p>
                                                <p style={{ color: MU, fontSize: 12, margin: 0 }}>{f.d}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* CTA */}
                            <button onClick={() => {
                                if (!user) { navigate('/auth/login?next=/sponsor/corporate'); return }
                                navigate('/sponsor/corporate')
                            }}
                                style={{
                                    width: '100%', padding: '20px', borderRadius: 18,
                                    background: G,
                                    color: M, fontWeight: 800, fontSize: 18,
                                    border: 'none', cursor: 'pointer', letterSpacing: '-0.01em',
                                    boxShadow: '0 10px 40px rgba(212,168,67,0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}>
                                🏢 Set Up Recurring Sponsorship →
                            </button>
                            <p style={{ color: MU, fontSize: 12, textAlign: 'center', margin: '12px 0 0' }}>
                                Full configuration on the next screen · Choose seats, interval, recognition preferences
                            </p>

                            {/* Existing sponsorship management */}
                            {hasExisting && (
                                <div style={{ textAlign: 'center', marginTop: 20 }}>
                                    <Link to="/sponsor/dashboard" style={{ color: G, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                                        Manage your existing sponsorships →
                                    </Link>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ── Signed-in user quick nav ──────────────────────────────────── */}
                {user && hasExisting && mode === 'a' && (
                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                        <Link to="/sponsor/dashboard" style={{ color: G, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                            You have active sponsorships — manage them →
                        </Link>
                    </div>
                )}
            </div>
        </div>
    )
}
